/**
 * Edit Your Reality - Vision Board Editor (Secondary Access)
 * 
 * This is NOT the primary experience.
 * Users come here occasionally to refine their vision.
 * 
 * Contains: Vaults, Evidence Logs, Future Letters, Settings
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft,
  Sparkles, 
  Heart, 
  Sun, 
  Home, 
  Compass, 
  Zap,
  Plus,
  Check,
  Edit3,
  FileText,
  Vault,
  TrendingUp
} from 'lucide-react';

// Reality categories
const CATEGORIES = [
  { id: 'wealth', title: 'Wealth', icon: Sparkles, color: '#CBA153' },
  { id: 'love', title: 'Love', icon: Heart, color: '#E8A4A4' },
  { id: 'body', title: 'Body', icon: Sun, color: '#7BA378' },
  { id: 'performance', title: 'Performance', icon: Zap, color: '#F59E0B' },
  { id: 'home', title: 'Home', icon: Home, color: '#A89080' },
  { id: 'freedom', title: 'Freedom', icon: Compass, color: '#7BA3B8' },
];

// Vault Card Component
const VaultCard = ({ vault, color, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(vault.name);
  const [target, setTarget] = useState(vault.target.toString());

  const progress = vault.current ? (vault.current / vault.target) * 100 : 0;

  const handleSave = () => {
    onUpdate({ ...vault, name, target: parseFloat(target) || 0 });
    setIsEditing(false);
  };

  return (
    <motion.div
      layout
      className="bg-[#1A1A18] rounded-xl p-5 border border-[#2C2C28]"
    >
      {isEditing ? (
        <div className="space-y-3">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Vault name"
            className="w-full bg-[#121210] border border-[#2C2C28] rounded-lg px-4 py-3 text-[#F2EFE9] text-sm focus:outline-none focus:border-[#4A6741]/50"
          />
          <input
            type="number"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="Target amount"
            className="w-full bg-[#121210] border border-[#2C2C28] rounded-lg px-4 py-3 text-[#F2EFE9] text-sm focus:outline-none focus:border-[#4A6741]/50"
          />
          <button
            onClick={handleSave}
            className="w-full py-2 rounded-lg bg-[#4A6741] text-[#F2EFE9] text-sm"
          >
            Save
          </button>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[#F2EFE9] text-sm font-light">{vault.name}</span>
            <button
              onClick={() => setIsEditing(true)}
              className="p-1 rounded hover:bg-[#2C2C28] transition-colors"
            >
              <Edit3 size={14} className="text-[#9E9A90]" />
            </button>
          </div>
          
          <div className="mb-2">
            <div className="h-2 bg-[#121210] rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(progress, 100)}%` }}
                transition={{ duration: 1, delay: 0.2 }}
                className="h-full rounded-full"
                style={{ backgroundColor: color }}
              />
            </div>
          </div>
          
          <div className="flex justify-between text-xs">
            <span className="text-[#9E9A90]">£{vault.current?.toLocaleString() || 0}</span>
            <span className="text-[#9E9A90]">£{vault.target?.toLocaleString()}</span>
          </div>
          
          <p className="text-[#9E9A90] text-xs mt-3 italic">
            This is already unfolding
          </p>
        </div>
      )}
    </motion.div>
  );
};

// Evidence Log Component
const EvidenceLog = ({ entries, color, onAdd }) => {
  const [newEntry, setNewEntry] = useState('');

  const handleAdd = () => {
    if (newEntry.trim()) {
      onAdd(newEntry);
      setNewEntry('');
    }
  };

  return (
    <div className="bg-[#1A1A18] rounded-xl p-5 border border-[#2C2C28]">
      <h3 className="text-[#F2EFE9] text-sm font-light mb-4 flex items-center gap-2">
        <Check size={14} style={{ color }} />
        Evidence of Abundance
      </h3>
      
      <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
        {entries.map((entry, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-start gap-2"
          >
            <div className="w-1.5 h-1.5 rounded-full mt-2" style={{ backgroundColor: color }} />
            <p className="text-[#9E9A90] text-sm">{entry}</p>
          </motion.div>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={newEntry}
          onChange={(e) => setNewEntry(e.target.value)}
          placeholder="Add evidence..."
          className="flex-1 bg-[#121210] border border-[#2C2C28] rounded-lg px-4 py-2 text-[#F2EFE9] text-sm focus:outline-none focus:border-[#4A6741]/50"
          onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
        />
        <button
          onClick={handleAdd}
          className="px-4 py-2 rounded-lg bg-[#4A6741] text-[#F2EFE9]"
        >
          <Plus size={16} />
        </button>
      </div>
    </div>
  );
};

// Future Letter Component
const FutureLetter = ({ letter, color, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(letter || '');

  return (
    <div className="bg-[#1A1A18] rounded-xl p-5 border border-[#2C2C28]">
      <h3 className="text-[#F2EFE9] text-sm font-light mb-4 flex items-center gap-2">
        <FileText size={14} style={{ color }} />
        Letter from Future You
      </h3>
      
      {isEditing ? (
        <div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write to yourself from the future..."
            className="w-full h-40 bg-[#121210] border border-[#2C2C28] rounded-lg px-4 py-3 text-[#F2EFE9] text-sm focus:outline-none focus:border-[#4A6741]/50 resize-none"
          />
          <button
            onClick={() => {
              onUpdate(content);
              setIsEditing(false);
            }}
            className="w-full mt-3 py-2 rounded-lg bg-[#4A6741] text-[#F2EFE9] text-sm"
          >
            Save Letter
          </button>
        </div>
      ) : (
        <div>
          {content ? (
            <p className="text-[#9E9A90] text-sm italic leading-relaxed">{content}</p>
          ) : (
            <p className="text-[#9E9A90]/50 text-sm italic">No letter yet...</p>
          )}
          <button
            onClick={() => setIsEditing(true)}
            className="mt-4 text-sm hover:text-[#F2EFE9] transition-colors"
            style={{ color }}
          >
            {content ? 'Edit letter' : 'Write a letter'}
          </button>
        </div>
      )}
    </div>
  );
};

// Category Section
const CategorySection = ({ category, data, onUpdate }) => {
  const Icon = category.icon;
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      layout
      className="border-b border-[#2C2C28] pb-6 mb-6 last:border-0"
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between py-2"
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${category.color}15` }}
          >
            <Icon size={18} style={{ color: category.color }} strokeWidth={1.5} />
          </div>
          <span className="text-[#F2EFE9] text-lg font-light">{category.title}</span>
        </div>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <ArrowLeft size={20} className="text-[#9E9A90] rotate-[-90deg]" />
        </motion.div>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pt-6 space-y-4">
              {/* Vaults */}
              <div>
                <h4 className="text-[#9E9A90] text-xs uppercase tracking-[0.15em] mb-3">
                  Abundance Vaults
                </h4>
                <div className="grid gap-3">
                  {(data.vaults || []).map((vault, i) => (
                    <VaultCard
                      key={i}
                      vault={vault}
                      color={category.color}
                      onUpdate={(updated) => {
                        const newVaults = [...(data.vaults || [])];
                        newVaults[i] = updated;
                        onUpdate({ ...data, vaults: newVaults });
                      }}
                    />
                  ))}
                  <button
                    onClick={() => {
                      const newVaults = [...(data.vaults || []), { name: 'New Goal', target: 10000, current: 0 }];
                      onUpdate({ ...data, vaults: newVaults });
                    }}
                    className="py-3 rounded-xl border border-dashed border-[#2C2C28] text-[#9E9A90] text-sm hover:border-[#4A6741]/50 transition-colors"
                  >
                    + Add Vault
                  </button>
                </div>
              </div>

              {/* Evidence Log */}
              <EvidenceLog
                entries={data.evidence || []}
                color={category.color}
                onAdd={(entry) => {
                  const newEvidence = [...(data.evidence || []), entry];
                  onUpdate({ ...data, evidence: newEvidence });
                }}
              />

              {/* Future Letter */}
              <FutureLetter
                letter={data.futureLetter}
                color={category.color}
                onUpdate={(letter) => onUpdate({ ...data, futureLetter: letter })}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Main Component
export const EditYourReality = () => {
  const navigate = useNavigate();
  const [categoryData, setCategoryData] = useState({});

  // Load data from localStorage (will be moved to backend)
  useEffect(() => {
    const saved = localStorage.getItem('abundance_reality_data');
    if (saved) {
      setCategoryData(JSON.parse(saved));
    }
  }, []);

  // Save data
  const handleUpdate = (categoryId, data) => {
    const newData = { ...categoryData, [categoryId]: data };
    setCategoryData(newData);
    localStorage.setItem('abundance_reality_data', JSON.stringify(newData));
  };

  return (
    <div className="min-h-screen bg-[#121210]">
      {/* Noise texture */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative z-10">
        {/* Header */}
        <header className="px-6 pt-10 pb-8">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => navigate('/abundance')}
              className="p-2 rounded-lg hover:bg-[#1A1A18] transition-colors"
              data-testid="back-to-reality"
            >
              <ArrowLeft size={24} className="text-[#9E9A90]" />
            </button>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-[#9E9A90] text-xs uppercase tracking-[0.25em] mb-4 font-light"
          >
            Vision Board
          </motion.p>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-['Playfair_Display'] text-4xl text-[#F2EFE9] tracking-tight"
          >
            Edit Your Reality
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-[#9E9A90] text-base mt-4 font-light"
          >
            Define and refine what you are creating
          </motion.p>
        </header>

        {/* Quick Access - Bank of Reclaim */}
        <div className="px-6 mb-8">
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            onClick={() => navigate('/bank-of-reclaim')}
            className="w-full p-5 rounded-2xl bg-[#1A1A18] border border-[#2C2C28] hover:border-[#CBA153]/30 transition-all duration-500 flex items-center justify-between"
            data-testid="bank-of-reclaim-link"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#CBA153]/10 flex items-center justify-center">
                <TrendingUp size={20} className="text-[#CBA153]" />
              </div>
              <div className="text-left">
                <p className="text-[#F2EFE9] font-light">Bank of Reclaim</p>
                <p className="text-[#9E9A90] text-sm">Your abundance reality</p>
              </div>
            </div>
            <ArrowLeft size={20} className="text-[#9E9A90] rotate-180" />
          </motion.button>
        </div>

        {/* Categories */}
        <main className="px-6 pb-24">
          {CATEGORIES.map((category, index) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + index * 0.05 }}
            >
              <CategorySection
                category={category}
                data={categoryData[category.id] || {}}
                onUpdate={(data) => handleUpdate(category.id, data)}
              />
            </motion.div>
          ))}
        </main>
      </div>
    </div>
  );
};

export default EditYourReality;
